import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitPage } from '../lit-page';
import { devWarn } from '../luna-common';
import { type ThemeMode } from '../../theming';
import { brainMark } from '../luna-logo/brain-mark';
import '../luna-board-art/luna-board-art';

const THEMES: readonly ThemeMode[] = ['blue', 'sand', 'slate'];
const THEME_LABELS: Record<ThemeMode, string> = {
  blue: 'Blue',
  sand: 'Sand',
  slate: 'Slate',
};

function isThemeMode(value: string): value is ThemeMode {
  return (THEMES as readonly string[]).includes(value);
}

/**
 * Live composed miniature of one theme (BR-82). One panel per ThemeMode; the
 * root carries its own `data-theme` so the whole miniature renders the true
 * tokens, never hard-coded hexes. Click/Enter/Space selects via the same
 * `luna-select` event the appearance switcher emits (one selection semantic).
 */
@customElement('luna-theme-preview')
export class LunaThemePreview extends LitPage {
  private _theme: ThemeMode = 'blue';

  @property({ attribute: 'theme' })
  get theme(): ThemeMode {
    return this._theme;
  }

  set theme(value: string | null) {
    const next = String(value ?? '');
    if (!isThemeMode(next)) {
      devWarn(`luna-theme-preview: unknown theme "${next}" — falling back to "blue"`);
      this._theme = 'blue';
      return;
    }
    this._theme = next;
  }

  @property({ type: Boolean }) selected = false;

  private select(): void {
    this.dispatchEvent(
      new CustomEvent('luna-select', {
        detail: { theme: this._theme },
        bubbles: true,
        composed: true,
      })
    );
  }

  private onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.select();
    }
  }

  content() {
    const label = THEME_LABELS[this._theme];

    return html`
      <button
        type="button"
        class="luna-theme-preview ${this.selected ? 'is-selected' : ''}"
        data-theme=${this._theme}
        aria-pressed=${this.selected ? 'true' : 'false'}
        aria-label=${`${label} theme`}
        @click=${this.select}
        @keydown=${this.onKeydown}
      >
        <span class="luna-theme-preview__window">
          <span class="luna-theme-preview__topbar">
            ${brainMark('luna-theme-preview')}
            <span class="luna-theme-preview__brand"
              ><span class="luna-theme-preview__brand-word">MyBrain</span
              ><span class="luna-theme-preview__brand-word luna-theme-preview__brand-word--accent"
                >Works</span
              ></span
            >
            <span class="luna-theme-preview__navpill">Home</span>
          </span>
          <span class="luna-theme-preview__card">
            <luna-board-art puzzle="pegs" size="62"></luna-board-art>
            <span class="luna-theme-preview__play">Play</span>
          </span>
          <span class="luna-theme-preview__actions">
            <span class="luna-theme-preview__btn">Undo</span>
            <span class="luna-theme-preview__btn">New</span>
          </span>
        </span>
        <span class="luna-theme-preview__label">${label}</span>
      </button>
    `;
  }

  styles() {
    return css`
      .luna-theme-preview {
        display: flex;
        flex-direction: column;
        gap: 12px;
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        padding: 14px;
        border: none;
        border-radius: var(--luna-radius-card);
        background: var(--mbw-surface-card);
        box-shadow:
          inset 0 0 0 1px var(--mbw-card-hairline),
          0 3px 0 var(--mbw-card-ledge);
        cursor: pointer;
        font-family: var(--mbw-font-sans);
        color: var(--mbw-text-body);
        text-align: left;
      }

      .luna-theme-preview:focus-visible {
        outline: none;
        box-shadow:
          inset 0 0 0 1px var(--mbw-card-hairline),
          0 3px 0 var(--mbw-card-ledge),
          0 0 0 5px #ffffff,
          0 0 0 7px var(--cds-focus);
      }

      /* Selected affordance: inset ring in the theme's own accent (derived —
         [DESIGNER REVIEW]). */
      .luna-theme-preview.is-selected {
        box-shadow:
          inset 0 0 0 3px var(--mbw-topbar-solid),
          0 3px 0 var(--mbw-card-ledge);
      }

      .luna-theme-preview__window {
        display: flex;
        flex-direction: column;
        gap: 8px;
        flex: 1;
        padding: 8px;
        border-radius: var(--luna-radius-card-sm);
        background: var(--mbw-page);
      }

      .luna-theme-preview__topbar {
        display: flex;
        align-items: center;
        gap: 6px;
        height: 26px;
        box-sizing: border-box;
        padding: 0 8px;
        border-radius: 6px;
        background: var(--mbw-topbar-solid);
        color: var(--mbw-onbar-text);
      }

      /* Miniature of the top-bar logo — same two-tone brain glyph, driven by
         this panel's own data-theme tokens (never hard-coded hexes). */
      .luna-theme-preview__brain {
        display: block;
        flex: none;
        height: 16px;
        width: auto;
      }

      .luna-theme-preview__brain-half--left {
        fill: var(--mbw-onbar-text);
      }

      .luna-theme-preview__brain-half--right {
        fill: var(--mbw-onbar-accent);
      }

      .luna-theme-preview__brain-line {
        fill: none;
        stroke: var(--mbw-topbar-solid);
      }

      .luna-theme-preview__brain-node {
        fill: var(--mbw-topbar-solid);
      }

      .luna-theme-preview__brand {
        flex: 1;
        font-size: 11px;
        font-weight: 600;
      }

      .luna-theme-preview__brand-word--accent {
        color: var(--mbw-onbar-accent);
      }

      .luna-theme-preview__navpill {
        padding: 2px 8px;
        border-radius: 6px;
        background: var(--mbw-onbar-pill);
        font-size: 10px;
        font-weight: 600;
      }

      .luna-theme-preview__card {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        flex: 1;
        min-height: 90px;
        border-radius: var(--luna-radius-card-sm);
        background: var(--mbw-surface-card);
        box-shadow: inset 0 0 0 1px var(--mbw-card-hairline);
      }

      .luna-theme-preview__play {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 22px;
        padding: 0 12px;
        border-radius: 6px;
        background: var(--mbw-btn-primary-fill); /* themed primary (BR-93) — matches the real button under the panel's data-theme */
        color: var(--mbw-onbar-text);
        font-size: 10px;
        font-weight: 700;
      }

      .luna-theme-preview__actions {
        display: flex;
        gap: 6px;
      }

      .luna-theme-preview__btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 1;
        height: 20px;
        border-radius: 4px;
        background: var(--mbw-btn-primary-fill); /* themed primary (BR-93) */
        color: var(--mbw-onbar-text);
        font-size: 9px;
        font-weight: 600;
      }

      .luna-theme-preview__label {
        font-size: 13px; /* pinned: figma Appearance preview label */
        font-weight: 600;
        line-height: 16px;
        color: var(--mbw-text-muted);
        text-align: center;
      }

      .luna-theme-preview.is-selected .luna-theme-preview__label {
        font-weight: 700;
      }

      /* Strip anatomy (≤1100px, BR-97): the panel becomes a single themed band —
         themed page background, mini top bar left, label right. Desktop-only
         parts (card, play, action buttons) hide; the window flattens so the
         top bar and label lay out on the band directly. Derived — [DESIGNER REVIEW]. */
      @media (max-width: 1100px) {
        .luna-theme-preview {
          flex-direction: row;
          align-items: center;
          gap: 12px;
          padding: 0 14px;
          background: var(--mbw-page);
        }

        .luna-theme-preview__window {
          display: contents;
        }

        .luna-theme-preview__topbar {
          flex: 0 0 auto;
        }

        .luna-theme-preview__card,
        .luna-theme-preview__play,
        .luna-theme-preview__actions {
          display: none;
        }

        .luna-theme-preview__label {
          margin-left: auto;
        }
      }
    `;
  }
}
